import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../services/api';
import { ArrowLeft, Plus, Edit, Trash2, Search, Loader2, Package, DollarSign, Calendar, TrendingDown } from 'lucide-react';

const ManageAssetsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState({
    asset_name: '',
    asset_type: 'Equipment',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_price: '',
    useful_life_years: 5,
    salvage_value: 0,
    depreciation_method: 'straight_line',
    notes: ''
  });

  const ASSET_TYPES = [
    'Equipment',
    'Furniture',
    'Vehicles',
    'Computers',
    'Software',
    'Buildings',
    'Leasehold Improvements',
    'Other'
  ];

  const DEPRECIATION_METHODS = [
    { value: 'straight_line', label: 'Straight Line' },
    { value: 'declining_balance', label: 'Declining Balance' }
  ];

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await accountingAPI.getDepreciableAssets();
      setAssets(response.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      alert('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const calculateDepreciation = (asset) => {
    const yearsElapsed = (new Date() - new Date(asset.purchase_date)) / (1000 * 60 * 60 * 24 * 365);

    if (asset.depreciation_method === 'straight_line') {
      const annualDepreciation = (asset.purchase_price - asset.salvage_value) / asset.useful_life_years;
      const totalDepreciation = Math.min(annualDepreciation * yearsElapsed, asset.purchase_price - asset.salvage_value);
      return totalDepreciation;
    }

    return 0; // Simplified for now
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price),
        salvage_value: parseFloat(formData.salvage_value),
        useful_life_years: parseInt(formData.useful_life_years)
      };

      if (editingAsset) {
        await accountingAPI.updateDepreciableAsset(editingAsset.id, payload);
        alert('Asset updated successfully');
      } else {
        await accountingAPI.createDepreciableAsset(payload);
        alert('Asset created successfully');
      }
      resetForm();
      fetchAssets();
    } catch (error) {
      console.error('Error saving asset:', error);
      alert('Failed to save asset');
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    try {
      await accountingAPI.deleteDepreciableAsset(assetId);
      alert('Asset deleted successfully');
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Failed to delete asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      asset_name: asset.asset_name || '',
      asset_type: asset.asset_type || 'Equipment',
      purchase_date: asset.purchase_date ? asset.purchase_date.split('T')[0] : '',
      purchase_price: asset.purchase_price || '',
      useful_life_years: asset.useful_life_years || 5,
      salvage_value: asset.salvage_value || 0,
      depreciation_method: asset.depreciation_method || 'straight_line',
      notes: asset.notes || ''
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      asset_name: '',
      asset_type: 'Equipment',
      purchase_date: new Date().toISOString().split('T')[0],
      purchase_price: '',
      useful_life_years: 5,
      salvage_value: 0,
      depreciation_method: 'straight_line',
      notes: ''
    });
    setEditingAsset(null);
    setShowAddModal(false);
  };

  const filteredAssets = assets.filter(asset =>
    asset.asset_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.asset_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalAssetValue = assets.reduce((sum, asset) => sum + (Number(asset.purchase_price) || 0), 0);
  const totalDepreciation = assets.reduce((sum, asset) => sum + (Number(calculateDepreciation(asset)) || 0), 0);
  const currentValue = totalAssetValue - totalDepreciation;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading assets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/accounting')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Accounting
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Depreciable Assets</h1>
              <p className="text-gray-600">Track fixed assets and depreciation</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-2xl font-semibold shadow-lg hover:from-green-700 hover:to-green-600 transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Asset
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-green-100 rounded-xl">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Assets</div>
                <div className="text-2xl font-bold text-gray-900">{assets.length}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-blue-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Purchase Value</div>
                <div className="text-2xl font-bold text-blue-600">${(Number(totalAssetValue) || 0).toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-red-100 rounded-xl">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Depreciation</div>
                <div className="text-2xl font-bold text-red-600">${(Number(totalDepreciation) || 0).toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-purple-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Current Value</div>
                <div className="text-2xl font-bold text-purple-600">${(Number(currentValue) || 0).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Assets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.length === 0 ? (
            <div className="col-span-full bg-white rounded-2xl shadow-sm p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-4">No assets found</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all"
              >
                Add Your First Asset
              </button>
            </div>
          ) : (
            filteredAssets.map((asset) => {
              const depreciation = calculateDepreciation(asset);
              const bookValue = asset.purchase_price - depreciation;

              return (
                <div key={asset.id} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{asset.asset_name}</h3>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                        {asset.asset_type}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Purchase Price:</span>
                      <span className="font-bold text-gray-900">${(Number(asset.purchase_price) || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Accumulated Depreciation:</span>
                      <span className="font-bold text-red-600">-${(Number(depreciation) || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                      <span className="text-gray-900 font-semibold">Book Value:</span>
                      <span className="font-bold text-green-600">${(Number(bookValue) || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      Purchased: {new Date(asset.purchase_date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <TrendingDown className="w-4 h-4" />
                      Useful Life: {asset.useful_life_years} years
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(asset)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-semibold hover:bg-blue-100 transition-all"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(asset.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <h3 className="font-bold text-blue-900 mb-2">💡 About Depreciable Assets</h3>
          <p className="text-sm text-blue-800">
            Depreciable assets are long-term business assets (equipment, furniture, vehicles, etc.) that lose value over time.
            The IRS allows you to deduct this depreciation as a business expense. Track all assets here for estimation purposes.
            Always consult with a licensed CPA about tax deductions, Section 179 deductions, bonus depreciation rules, and proper GAAP accounting.
          </p>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.asset_name}
                    onChange={(e) => setFormData({...formData, asset_name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., MacBook Pro, Office Desk, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Asset Type *
                  </label>
                  <select
                    required
                    value={formData.asset_type}
                    onChange={(e) => setFormData({...formData, asset_type: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  >
                    {ASSET_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Purchase Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Purchase Price *
                    </label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Useful Life (years) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.useful_life_years}
                      onChange={(e) => setFormData({...formData, useful_life_years: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Salvage Value
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salvage_value}
                      onChange={(e) => setFormData({...formData, salvage_value: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Depreciation Method *
                  </label>
                  <select
                    required
                    value={formData.depreciation_method}
                    onChange={(e) => setFormData({...formData, depreciation_method: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  >
                    {DEPRECIATION_METHODS.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    placeholder="Additional details about this asset"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-2xl font-semibold hover:bg-green-700 transition-all"
                >
                  {editingAsset ? 'Save Changes' : 'Add Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAssetsPage;
